import React, { useState } from "react";
import { Edit2, X } from "lucide-react";
import supabase from "../supabaseClient";
import { createStripedGradient } from "../utils/calendarUtils";

const GroupsTab = ({ groups, members, onReload }) => {
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editMemberIds, setEditMemberIds] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newMemberIds, setNewMemberIds] = useState([]);

  const createStripedPreview = (memberIds) => {
    const colors = memberIds
      .map((id) => members.find((m) => m.id === id)?.color)
      .filter(Boolean);

    if (colors.length === 0) return "#6b7280";
    return createStripedGradient(colors);
  };

  const handleEdit = (group) => {
    setEditingId(group.id);
    setEditName(group.name);
    setEditMemberIds(group.member_ids || []);
  };

  const handleSaveEdit = async () => {
    if (!editName.trim() || editMemberIds.length === 0) {
      alert("Please provide a name and select at least one member");
      return;
    }

    try {
      const { error } = await supabase
        .from("family_groups")
        .update({ name: editName, member_ids: editMemberIds })
        .eq("id", editingId);

      if (error) throw error;

      setEditingId(null);
      await onReload();
    } catch (error) {
      console.error("Error updating group:", error);
      alert("Error updating group: " + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this group? This will also delete all group events."))
      return;

    try {
      const { error } = await supabase
        .from("family_groups")
        .delete()
        .eq("id", id);

      if (error) throw error;

      await onReload();
    } catch (error) {
      console.error("Error deleting group:", error);
      alert("Error deleting group: " + error.message);
    }
  };

  const handleAdd = async () => {
    if (!newName.trim() || newMemberIds.length === 0) {
      alert("Please provide a name and select at least one member");
      return;
    }

    try {
      const { error } = await supabase
        .from("family_groups")
        .insert([{ name: newName, member_ids: newMemberIds }]);

      if (error) throw error;

      setAdding(false);
      setNewName("");
      setNewMemberIds([]);
      await onReload();
    } catch (error) {
      console.error("Error adding group:", error);
      alert("Error adding group: " + error.message);
    }
  };

  const toggleMember = (memberId, isEdit) => {
    if (isEdit) {
      setEditMemberIds((prev) =>
        prev.includes(memberId)
          ? prev.filter((id) => id !== memberId)
          : [...prev, memberId]
      );
    } else {
      setNewMemberIds((prev) =>
        prev.includes(memberId)
          ? prev.filter((id) => id !== memberId)
          : [...prev, memberId]
      );
    }
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {groups.map((group) => {
        const gradient = createStripedPreview(group.member_ids || []);
        const isGradient = gradient.startsWith("linear-gradient");

        return (
          <div key={group.id} className="p-3 sm:p-4 border border-gray-200 rounded-lg">
            {editingId === group.id ? (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded flex-shrink-0"
                    style={
                      isGradient
                        ? { background: createStripedPreview(editMemberIds) }
                        : { backgroundColor: createStripedPreview(editMemberIds) }
                    }
                  />
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="flex-1 px-3 py-2 sm:py-3 border border-gray-300 rounded-lg text-base"
                  />
                </div>
                <div className="pl-0 sm:pl-14 mb-3">
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    Members:
                  </div>
                  <div className="space-y-1">
                    {members.map((member) => (
                      <label
                        key={member.id}
                        className="flex items-center gap-2 cursor-pointer py-1 touch-manipulation"
                      >
                        <input
                          type="checkbox"
                          checked={editMemberIds.includes(member.id)}
                          onChange={() => toggleMember(member.id, true)}
                          className="rounded w-5 h-5"
                        />
                        <span className="text-sm sm:text-base">{member.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveEdit}
                    className="px-4 py-2 sm:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium touch-manipulation text-sm sm:text-base"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="px-4 py-2 sm:py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium touch-manipulation text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded flex-shrink-0"
                    style={
                      isGradient
                        ? { background: gradient }
                        : { backgroundColor: gradient }
                    }
                  />
                  <div className="flex-1 font-medium text-sm sm:text-base">{group.name}</div>
                  <button
                    onClick={() => handleEdit(group)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg touch-manipulation"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(group.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg touch-manipulation"
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="pl-0 sm:pl-14">
                  <div className="text-xs sm:text-sm text-gray-600">
                    Members:{" "}
                    {(group.member_ids || [])
                      .map((id) => members.find((m) => m.id === id)?.name)
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })}

      {adding ? (
        <div className="p-3 sm:p-4 border-2 border-purple-300 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-10 h-10 sm:w-12 sm:h-12 rounded flex-shrink-0"
              style={{ background: createStripedPreview(newMemberIds) }}
            />
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Group name"
              className="flex-1 px-3 py-2 sm:py-3 border border-gray-300 rounded-lg text-base"
            />
          </div>
          <div className="pl-0 sm:pl-14 mb-3">
            <div className="text-sm font-medium text-gray-700 mb-2">
              Members:
            </div>
            <div className="space-y-1">
              {members.map((member) => (
                <label
                  key={member.id}
                  className="flex items-center gap-2 cursor-pointer py-1 touch-manipulation"
                >
                  <input
                    type="checkbox"
                    checked={newMemberIds.includes(member.id)}
                    onChange={() => toggleMember(member.id, false)}
                    className="rounded w-5 h-5"
                  />
                  <span className="text-sm sm:text-base">{member.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              className="px-4 py-2 sm:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium touch-manipulation text-sm sm:text-base"
            >
              Add
            </button>
            <button
              onClick={() => {
                setAdding(false);
                setNewName("");
                setNewMemberIds([]);
              }}
              className="px-4 py-2 sm:py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium touch-manipulation text-sm sm:text-base"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="w-full py-3 sm:py-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-400 hover:text-purple-600 font-medium touch-manipulation text-sm sm:text-base"
        >
          + Add Group
        </button>
      )}
    </div>
  );
};

export default GroupsTab;
